import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import destIbiza from "@/assets/dest-marbella-new.webp";
import destDubai from "@/assets/dest-dubai-new.webp";
import destParis from "@/assets/dest-paris-new.webp";
import destStTropez from "@/assets/dest-sttropez-v2.webp";
import destMarbella from "@/assets/dest-ibiza.webp";

const DestinationsSection = () => {
  const { t } = useLanguage();

  const destinations = [
    {
      name: "Ibiza",
      subtitle: t("Ibiza, Spain", "Ibiza, Spanje"),
      description: t(
        "A Mediterranean island known for its vibrant rhythm and hidden coves, Ibiza unfolds through private villas, open waters and sunlit shores, where each experience moves effortlessly between energy and calm.",
        "Een Mediterraan eiland bekend om zijn levendige ritme en verborgen baaien. Ibiza ontvouwt zich via privévilla's, open wateren en zonovergoten kusten, waar elke ervaring moeiteloos beweegt tussen energie en rust."
      ),
      image: destIbiza,
      link: "/ibiza",
    },
    {
      name: "Marbella",
      subtitle: t("Marbella, Spain", "Marbella, Spanje"),
      description: t(
        "A sun-drenched Mediterranean enclave where sea, mountains and golden light meet, Marbella blends relaxed coastal living with timeless sophistication, revealing a destination shaped by elegance, privacy and effortless rhythm.",
        "Een zonovergoten Mediterrane enclave waar zee, bergen en gouden licht samenkomen. Marbella combineert ontspannen kustleven met tijdloze verfijning, een bestemming gevormd door elegantie, privacy en moeiteloos ritme."
      ),
      image: destMarbella,
      link: "/marbella",
    },
    {
      name: "Saint-Tropez",
      subtitle: t("Saint-Tropez, France", "Saint-Tropez, Frankrijk"),
      description: t(
        "Framed by the glittering waters of the French Riviera, Saint-Tropez blends Mediterranean charm with timeless glamour, where sun-lit harbours, elegant beach clubs and a relaxed coastal rhythm define the experience.",
        "Omlijst door de glinsterende wateren van de Franse Rivièra, combineert Saint-Tropez Mediterrane charme met tijdloze glamour, waar zonovergoten havens, elegante beachclubs en een ontspannen kustritme de ervaring definiëren."
      ),
      image: destStTropez,
      link: "/saint-tropez",
    },
    {
      name: "Dubai",
      subtitle: t("Dubai, UAE", "Dubai, VAE"),
      description: t(
        "A city of ambition without limits, Dubai offers a world of possibility from the Marina to the desert — orchestrated to the highest standard with seamless precision.",
        "Een stad van grenzeloze ambitie. Dubai biedt een wereld van mogelijkheden van de Marina tot de woestijn — georkestreerd volgens de hoogste standaard met naadloze precisie."
      ),
      image: destDubai,
      link: "/dubai",
    },
    {
      name: "Paris",
      subtitle: t("Paris, France", "Parijs, Frankrijk"),
      description: t(
        "Elegance perfected. From grand boulevards to hidden courtyards, Paris reveals itself through refined experiences arranged with discretion and care.",
        "Elegantie geperfectioneerd. Van grote boulevards tot verborgen binnenplaatsen, Parijs onthult zich door verfijnde ervaringen geregeld met discretie en zorg."
      ),
      image: destParis,
      link: "/paris",
    },
  ];

  return (
    <section id="destinations" className="section-spacing bg-background">
      <div className="container-luxury">
        <motion.div
          className="mb-20 md:mb-28 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="font-label text-bronze block mb-8">{t("Destinations", "Bestemmingen")}</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-8">
            {t("Where we operate", "Waar wij opereren")}
          </h2>
          <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.8] max-w-3xl mx-auto">
            {t(
              "From the timeless avenues of Paris to the sun-lit shores of Saint-Tropez, Marbella and Ibiza, and the striking skyline of Dubai, Arzani Group curates experiences in some of the world's most sought-after destinations. Each place carries its own rhythm, atmosphere and character.",
              "Van de tijdloze lanen van Parijs tot de zonovergoten kusten van Saint-Tropez, Marbella en Ibiza, en de indrukwekkende skyline van Dubai — Arzani Group stelt ervaringen samen op enkele van 's werelds meest gewilde bestemmingen. Elke plek heeft zijn eigen ritme, sfeer en karakter."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {destinations.slice(0, 3).map((dest, index) => (
            <DestinationCard key={dest.name} dest={dest} index={index} />
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mt-8 md:mt-10 max-w-[66%] mx-auto">
          {destinations.slice(3, 5).map((dest, index) => (
            <DestinationCard key={dest.name} dest={dest} index={index + 3} />
          ))}
        </div>
      </div>
    </section>
  );
};

const DestinationCard = ({
  dest,
  index,
}: {
  dest: { name: string; subtitle: string; description: string; image: string; link?: string };
  index: number;
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  return (
  <motion.div
    className="group cursor-pointer"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{
      duration: 1,
      delay: 0.1 * index,
      ease: [0.22, 1, 0.36, 1],
    }}
  >
    <div className="image-architectural aspect-[4/5] overflow-hidden relative mb-6">
      <img
        src={dest.image}
        alt={`${dest.name} destination`}
        className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.03]"
        loading="lazy"
      />
    </div>
    <span className="font-label text-muted-foreground block mb-3 text-[9px]">
      {dest.subtitle}
    </span>
    <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4 tracking-wide uppercase">
      {dest.name}
    </h3>
    <p className="font-body text-muted-foreground text-sm leading-[1.8] mb-5">
      {dest.description}
    </p>
    <button
      onClick={() => {
        if (dest.link) {
          navigate(dest.link);
        } else {
          const el = document.getElementById("inquire");
          el?.scrollIntoView({ behavior: "smooth" });
        }
      }}
      className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px]"
    >
      {t("Choose", "Kies")}
    </button>
  </motion.div>
  );
};

export default DestinationsSection;
